/**
 * Phase 8 · Cluster C-48 — Agent route context hook.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.A
 *
 * Emits a typed `AgentRouteContext` for the current route: the route
 * pattern, the resolved entity (when the URL has one) and the set of
 * `ACTIONS_FOR_ROUTE` chips available on this page.
 *
 * Entity labels are opt-in: callers on a record detail page pass the
 * resolved entity via `trackEntity(entity)` — typically from a `useEffect`
 * that fires once the record finishes loading. The last 5 visited
 * entities are persisted to `localStorage['ggj.agent.recent']` and
 * surfaced on `recentEntities`.
 *
 * This hook does NOT render anything; composing it with `AgentQuickActions`
 * produces the chip row.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { ACTIONS_FOR_ROUTE } from '../context/actionsForRoute';
import type {
  AgentEntity,
  AgentQuickAction,
  AgentRecentEntity,
  AgentRouteContext,
} from '../context/types';

const RECENT_STORAGE_KEY = 'ggj.agent.recent';
const RECENT_CAP = 5;

/**
 * Ordered list of route patterns we attempt to match the current pathname
 * against, longest-specific first. Keep in sync with the keys of
 * `ACTIONS_FOR_ROUTE`.
 */
const ROUTE_PATTERNS: Array<{ pattern: string; scopeKind: AgentRouteContext['scopeKind'] }> = [
  // Record detail pages first — `matchPath` greedy-matches so the static
  // segment check ensures `/requisitions/new` does NOT fall into `:id`.
  { pattern: '/requisitions/:id', scopeKind: 'record' },
  { pattern: '/candidates/:id', scopeKind: 'record' },
  { pattern: '/applications/:id', scopeKind: 'record' },
  { pattern: '/interviews/:id', scopeKind: 'record' },
  { pattern: '/clients/:id', scopeKind: 'record' },

  // Smart Match canvas — canvas scope.
  { pattern: '/smart-match/*', scopeKind: 'canvas' },

  // List pages (chip set driven by the static prefix).
  { pattern: '/requisitions', scopeKind: 'list' },
  { pattern: '/candidates', scopeKind: 'list' },

  // Home fallback.
  { pattern: '/', scopeKind: 'list' },
];

/** "new" / "create" reserved segments that must never resolve to an entity id. */
const RESERVED_ID_SEGMENTS = new Set(['new', 'create', 'edit', 'intake']);

function loadRecent(): AgentRecentEntity[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const rows = parsed.filter(
      (r): r is AgentRecentEntity =>
        !!r &&
        typeof r === 'object' &&
        typeof (r as AgentRecentEntity).kind === 'string' &&
        typeof (r as AgentRecentEntity).id === 'string' &&
        typeof (r as AgentRecentEntity).label === 'string' &&
        typeof (r as AgentRecentEntity).visitedAt === 'string',
    );
    return rows.slice(0, RECENT_CAP);
  } catch {
    return [];
  }
}

function saveRecent(rows: AgentRecentEntity[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(rows.slice(0, RECENT_CAP)));
  } catch {
    // Quota / SecurityError — non-fatal. Recent entities are advisory.
  }
}

/**
 * Resolve the current pathname to the most specific route pattern we know
 * about. Falls back to the raw pathname so chips still render ("none") on
 * unknown routes.
 */
function resolveRoutePattern(pathname: string): {
  route: string;
  scopeKind: AgentRouteContext['scopeKind'];
} {
  for (const row of ROUTE_PATTERNS) {
    const match = matchPath({ path: row.pattern, end: row.pattern !== '/smart-match/*' }, pathname);
    if (!match) continue;
    // Reserved segment guard: '/requisitions/new' matches '/requisitions/:id'
    // but is not actually a record — bail out so the list-scope chips apply.
    const paramId = (match.params as { id?: string }).id;
    if (paramId && RESERVED_ID_SEGMENTS.has(paramId)) continue;
    return { route: row.pattern, scopeKind: row.scopeKind };
  }
  return { route: pathname, scopeKind: 'list' };
}

export interface UseAgentRouteContextReturn extends AgentRouteContext {
  /**
   * Called from a record detail page once it has resolved the entity
   * label/title (usually in a `useEffect` after the react-query data
   * arrives). Pushes the entity to the recent list and surfaces it as
   * `entity` for the current render.
   */
  trackEntity: (entity: AgentEntity) => void;
}

/**
 * Hook consumed by `AgentQuickActions` and `useAgentThread` (via the
 * C-48 context-block injection). Stable across route re-renders — the
 * returned object's chips and `trackEntity` are memoised.
 */
export function useAgentRouteContext(): UseAgentRouteContextReturn {
  const location = useLocation();
  const params = useParams();
  const { route, scopeKind } = useMemo(() => resolveRoutePattern(location.pathname), [
    location.pathname,
  ]);

  const [recentEntities, setRecentEntities] = useState<AgentRecentEntity[]>(() => loadRecent());
  const [trackedEntity, setTrackedEntity] = useState<AgentEntity | null>(null);

  // Clear tracked entity on route change — the detail page re-calls
  // trackEntity on mount once its data arrives. Without this reset we
  // would leak the previous page's entity into the new route's prompt.
  useEffect(() => {
    setTrackedEntity(null);
  }, [location.pathname]);

  const trackEntity = useCallback((entity: AgentEntity) => {
    if (!entity.id) return;
    setTrackedEntity(entity);
    setRecentEntities((prev) => {
      const next: AgentRecentEntity[] = [
        { ...entity, visitedAt: new Date().toISOString() },
        ...prev.filter((r) => !(r.kind === entity.kind && r.id === entity.id)),
      ].slice(0, RECENT_CAP);
      saveRecent(next);
      return next;
    });
  }, []);

  const entity = useMemo<AgentEntity | undefined>(() => {
    if (trackedEntity) return trackedEntity;
    // Fallback: if the route has an :id param but the detail page has not
    // yet called trackEntity, synthesise a stub entity using the raw id.
    // `label` is set to the id so the chips still substitute something
    // useful if clicked before hydration finishes.
    if (scopeKind !== 'record') return undefined;
    const kind = kindFromRoute(route);
    const rawId = params.id;
    if (!kind || !rawId) return undefined;
    return { kind, id: rawId, label: rawId };
  }, [trackedEntity, scopeKind, route, params.id]);

  const availableActions = useMemo<AgentQuickAction[]>(() => {
    const raw = ACTIONS_FOR_ROUTE[route] ?? [];
    if (entity) return raw;
    return raw.filter((a) => !a.requiresEntity);
  }, [route, entity]);

  return useMemo(
    () => ({
      route,
      scopeKind,
      entity,
      availableActions,
      recentEntities,
      trackEntity,
    }),
    [route, scopeKind, entity, availableActions, recentEntities, trackEntity],
  );
}

function kindFromRoute(route: string): AgentEntity['kind'] | null {
  if (route.startsWith('/requisitions')) return 'requisition';
  if (route.startsWith('/candidates')) return 'candidate';
  if (route.startsWith('/applications')) return 'application';
  if (route.startsWith('/interviews')) return 'interview';
  if (route.startsWith('/clients')) return 'client';
  return null;
}

/**
 * Build the multi-line system-level context block injected into every
 * turn's first message. Matches the exact format required by §34.2.A:
 *
 *   CONTEXT
 *   - route: /requisitions/:id
 *   - scope: record
 *   - entity: requisition REQ-… "Senior PM"
 *   - recent: [candidate c-1 "Jane", …]
 *   Prefer actions scoped to that entity.
 */
export function formatAgentContextBlock(ctx: AgentRouteContext): string {
  const lines: string[] = ['CONTEXT'];
  lines.push(`- route: ${ctx.route}`);
  lines.push(`- scope: ${ctx.scopeKind}`);
  if (ctx.entity) {
    lines.push(`- entity: ${ctx.entity.kind} ${ctx.entity.id} "${ctx.entity.label}"`);
  } else {
    lines.push('- entity: (none)');
  }
  if (ctx.recentEntities.length > 0) {
    const recent = ctx.recentEntities
      .slice(0, RECENT_CAP)
      .map((r) => `${r.kind} ${r.id} "${r.label}"`)
      .join(', ');
    lines.push(`- recent: [${recent}]`);
  } else {
    lines.push('- recent: []');
  }
  lines.push('Prefer actions scoped to that entity.');
  return lines.join('\n');
}
